import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import { GlobalRadioButtons, RadioOption } from '../../../components/GlobalComponents/GlobalRadioButtons';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

interface UploadedFile {
  file_name: string;
  key: string;
  cdn_url: string;
  dev_url: string;
  mime_type: string;
  size: number;
  type: 'image' | 'pdf' | 'other';
}

interface NoticeFormData {
  title: string;
  body: string;
  priority: string;
  notify: string;
  existingFiles: UploadedFile[];
}

interface AddNoticeFormProps {
  open: boolean;
  onClose: () => void;
  noticeId?: number | null;
  onSuccess: (shouldResetPage?: boolean) => void;
}

const AddNoticeForm: React.FC<AddNoticeFormProps> = ({ open, onClose, noticeId, onSuccess }) => {
  const toast                                 = useRef<Toast>(null);
  const fileInputRef                          = useRef<HTMLInputElement>(null);
  const [formData, setFormData]               = useState<NoticeFormData>({
    title: '', body: '', priority: 'Medium', notify: 'All', existingFiles: []
  });
  const [newFiles, setNewFiles]               = useState<File[]>([]);
  const [filePreviewUrls, setFilePreviewUrls] = useState<string[]>([]);
  const [loading, setLoading]                 = useState(false);
  const [fetching, setFetching]               = useState(false); // separate state for initial data fetch
  const [isClosing, setIsClosing]             = useState(false);

  useEffect(() => {
    if (open && noticeId) {
      fetchNoticeDetails();
    } else if (open) {
      resetForm();
    }
  }, [open, noticeId]);

  useEffect(() => {
    return () => filePreviewUrls.forEach(url => URL.revokeObjectURL(url));
  }, [filePreviewUrls]);

  useEffect(() => {
    if (isClosing) {
      document.querySelector('.p-dialog')?.classList.add('dialog-closing');
      document.querySelector('.p-dialog-mask')?.classList.add('mask-closing');
    }
  }, [isClosing]);

  const fetchNoticeDetails = async () => {
    setFetching(true);
    try {
      const response = await apiClient.get(`noticeboard?notice_id=${noticeId}`);
      if (response.data.status === 'success') {
        const notice = response.data.notice;

        // ── Fix: use notice.broadcast_user_type as source of truth
        // not notifications[0].broadcast_user_type which may not exist
        const rawBroadcast = notice.broadcast_user_type ?? 'all';
        const notifyValue  = rawBroadcast.charAt(0).toUpperCase() + rawBroadcast.slice(1);

        setFormData({
          title:         notice.title         ?? '',
          body:          notice.body          ?? '',
          priority:      notice.priority      ?? 'Medium',
          notify:        notifyValue,
          existingFiles: Array.isArray(notice.images) ? notice.images : [],
        });

        // Reset new files on every edit open
        setNewFiles([]);
        setFilePreviewUrls([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load notice details', life: 5000 });
        handleClose();
      }
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load notice details', life: 5000 });
      handleClose();
    } finally {
      setFetching(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', body: '', priority: 'Medium', notify: 'All', existingFiles: [] });
    setNewFiles([]);
    setFilePreviewUrls([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (loading) return; // prevent close while submitting
    setIsClosing(true);
    setTimeout(() => { onClose(); setIsClosing(false); }, 400);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;

    const total = formData.existingFiles.length + newFiles.length + selected.length;
    if (total > 5) {
      toast.current?.show({ severity: 'warn', summary: 'Limit Exceeded', detail: 'Maximum 5 files allowed.', life: 4000 });
      return;
    }

    const oversized = selected.find(f => f.size > 10 * 1024 * 1024);
    if (oversized) {
      toast.current?.show({ severity: 'warn', summary: 'File Too Large', detail: `${oversized.name} exceeds 10MB limit.`, life: 4000 });
      return;
    }

    const previews = selected.map(f => f.type.startsWith('image/') ? URL.createObjectURL(f) : '');
    setNewFiles(prev => [...prev, ...selected]);
    setFilePreviewUrls(prev => [...prev, ...previews]);

    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeNewFile = (index: number) => {
    URL.revokeObjectURL(filePreviewUrls[index]);
    setNewFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      existingFiles: prev.existingFiles.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Title is required', life: 5000 });
      return;
    }
    if (!formData.body.trim()) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Body is required', life: 5000 });
      return;
    }

    setLoading(true);
    try {
      const isEdit      = !!noticeId;
      const apiEndpoint = isEdit ? `noticeboard/${noticeId}` : 'noticeboard';
      const method      = isEdit ? 'put' : 'post';

      const payload = new FormData();
      payload.append('title',               formData.title);
      payload.append('body',                formData.body);
      payload.append('priority',            formData.priority);
      payload.append('broadcast_user_type', formData.notify.toLowerCase());

      // Always append new files
      newFiles.forEach(file => payload.append('files[]', file));

      // ── Fix: ALWAYS send existing_images on edit
      // even if empty array — so backend knows to clear files if all removed
      if (isEdit) {
        payload.append('existing_images', JSON.stringify(formData.existingFiles));
      }

      await apiClient[method](apiEndpoint, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: isEdit ? 'Notice updated successfully!' : 'Notice added successfully!',
        life: 5000
      });

      setTimeout(() => {
        resetForm();
        onSuccess(!isEdit); // reset page only on add
        handleClose();
      }, 300);

    } catch (err: any) {
      const message = err.response?.data?.message || `Failed to ${noticeId ? 'update' : 'add'} notice`;
      toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const priorityOptions: RadioOption[] = [
    { value: 'High',   label: 'High'   },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low',    label: 'Low'    },
  ];

  const notifyOptions: RadioOption[] = [
    { value: 'Student', label: 'Students' },
    { value: 'Teacher', label: 'Teachers' },
    { value: 'All',     label: 'All'      },
  ];

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div style={{
        width: '54px', height: '54px', borderRadius: '14px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontSize: '24px', fontWeight: 700,
        boxShadow: '0 8px 24px rgba(59,130,246,0.3)', flexShrink: 0, border: '3px solid white'
      }}>
        <i className={noticeId ? 'pi pi-pencil' : 'pi pi-bell'} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
          {noticeId ? 'Edit Notice' : 'Add New Notice'}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          {noticeId ? 'Update notice details below' : 'Create a new notice for your organization'}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '800px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)', overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)',
          borderRadius: '0 0 24px 24px', borderTop: 'none'
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)',
          borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)',
          margin: '-12px -12px 0 -12px'
        }}
        modal dismissableMask={!loading} draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; animation: maskFadeIn 0.35s ease; }
          .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
          @keyframes maskFadeIn { from { opacity:0; } to { opacity:1; } }
          @keyframes maskFadeOut { from { opacity:1; } to { opacity:0; } }
          .p-dialog { animation: dialogOpen 0.35s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25) !important; }
          .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4,0,0.2,1) !important; }
          @keyframes dialogOpen { from { opacity:0; transform:scale(0.92) translateY(-20px); } to { opacity:1; transform:scale(1) translateY(0); } }
          @keyframes dialogClose { from { opacity:1; transform:scale(1) translateY(0); } to { opacity:0; transform:scale(0.88) translateY(25px); } }
          .file-upload-zone:hover { border-color: #3b82f6 !important; background: rgba(59,130,246,0.05) !important; }
          .file-item:hover .file-remove-btn { opacity: 1 !important; }
        `}</style>

        {/* ── Fetching state (edit mode loading) ── */}
        {fetching ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '16px' }}>
            <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>Loading notice details...</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Notice Information */}
            <div style={{ background: 'rgba(255,255,255,0.8)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
              <SectionHeader icon="pi pi-info-circle" title="Notice Information" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <GlobalInputField
                  label="Title" value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter notice title" required disabled={loading} icon="pi pi-pencil"
                />
                <GlobalTextArea
                  label="Body" value={formData.body}
                  onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Enter your notice description here..."
                  required disabled={loading} rows={6} autoResize
                />
              </div>
            </div>

            {/* Attachments */}
            <div style={{ background: 'rgba(255,255,255,0.8)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
              <SectionHeader icon="pi pi-paperclip" title="Attachments" />

              {/* Upload Zone */}
              <div
                className="file-upload-zone"
                onClick={() => !loading && fileInputRef.current?.click()}
                style={{
                  border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '24px',
                  textAlign: 'center', cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease', background: 'rgba(248,250,252,0.8)', opacity: loading ? 0.6 : 1
                }}
              >
                <i className="pi pi-cloud-upload" style={{ fontSize: '28px', color: '#3b82f6', marginBottom: '8px', display: 'block' }} />
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Click to upload files</div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                  Images or PDFs • Max 5 files • Max 10MB each
                </div>
                <input
                  ref={fileInputRef} type="file" multiple hidden
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                  onChange={handleFileChange} disabled={loading}
                />
              </div>

              {/* Existing Files (edit mode) */}
              {formData.existingFiles.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '8px' }}>
                    SAVED FILES
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {formData.existingFiles.map((file, index) => (
                      <FileChip
                        key={index}
                        name={file.file_name}
                        type={file.type}
                        previewUrl={file.type === 'image' ? file.cdn_url : ''}
                        onRemove={() => removeExistingFile(index)}
                        disabled={loading}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* New Files */}
              {newFiles.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '8px' }}>
                    NEW FILES
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {newFiles.map((file, index) => (
                      <FileChip
                        key={index}
                        name={file.name}
                        type={file.type.startsWith('image/') ? 'image' : 'pdf'}
                        previewUrl={filePreviewUrls[index]}
                        onRemove={() => removeNewFile(index)}
                        disabled={loading}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* File count indicator */}
              {(formData.existingFiles.length + newFiles.length) > 0 && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#9ca3af', textAlign: 'right' }}>
                  {formData.existingFiles.length + newFiles.length} / 5 files
                </div>
              )}
            </div>

            {/* Settings */}
            <div style={{ background: 'rgba(255,255,255,0.8)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
              <SectionHeader icon="pi pi-cog" title="Settings" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <GlobalRadioButtons
                  label="Priority" options={priorityOptions} value={formData.priority}
                  onChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                  required disabled={loading} layout="horizontal"
                />
                <GlobalRadioButtons
                  label="Notify" options={notifyOptions} value={formData.notify}
                  onChange={(value) => setFormData(prev => ({ ...prev, notify: value }))}
                  required disabled={loading} layout="horizontal"
                />
              </div>
            </div>

            {/* Submit */}
            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <GlobalButton
                label={loading ? (noticeId ? 'Updating...' : 'Adding...') : (noticeId ? 'Update Notice' : 'Add Notice')}
                onClick={handleSubmit} variant="primary" size="medium"
                disabled={loading} loading={loading} fullWidth={false}
              />
            </div>

          </div>
        )}
      </Dialog>
    </>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '10px',
    marginBottom: '16px', paddingBottom: '12px',
    borderBottom: '1px solid rgba(229,231,235,0.6)'
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '32px', height: '32px', borderRadius: '8px',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white'
    }}>
      <i className={icon} style={{ fontSize: '15px' }} />
    </div>
    <span style={{ color: '#1e3a8a', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
      {title}
    </span>
  </div>
);

const FileChip: React.FC<{
  name: string; type: string; previewUrl: string;
  onRemove: () => void; disabled: boolean;
}> = ({ name, type, previewUrl, onRemove, disabled }) => (
  <div className="file-item" style={{
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '6px 10px', borderRadius: '8px', position: 'relative',
    background: type === 'image' ? 'rgba(59,130,246,0.08)' : 'rgba(239,68,68,0.08)',
    border: `1px solid ${type === 'image' ? 'rgba(59,130,246,0.2)' : 'rgba(239,68,68,0.2)'}`,
    maxWidth: '200px'
  }}>
    {type === 'image' && previewUrl ? (
      <img src={previewUrl} alt={name} style={{ width: '28px', height: '28px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }} />
    ) : (
      <i className="pi pi-file-pdf" style={{ fontSize: '20px', color: '#ef4444', flexShrink: 0 }} />
    )}
    <span style={{ fontSize: '12px', fontWeight: 500, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {name.length > 20 ? name.substring(0, 20) + '...' : name}
    </span>
    {!disabled && (
      <button
        className="file-remove-btn"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          display: 'flex', alignItems: 'center', opacity: 0,
          transition: 'opacity 0.2s ease', flexShrink: 0
        }}
      >
        <i className="pi pi-times" style={{ fontSize: '11px', color: '#6b7280' }} />
      </button>
    )}
  </div>
);

export default AddNoticeForm;
